import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { buildSearchParamsAuto } from '../utils/queryParams';

const AFFILIATIONS_TAG_TYPE = 'Affiliations';

interface AffiliationProvider {
  provider_id: string;
  name: string;
  specialty: string;
  [key: string]: any;
}

interface PaginatedResponse<T> {
  results: T[];
  count: number;
  next?: string | null;
  previous?: string | null;
}

interface GetReferringOutParams {
  providerId: string;
  page?: number;
  pageSize?: number;
  sort?: string | null;
  order?: 'asc' | 'desc' | null;
  sort_search?: string | null;
  sort_value?: string | null;
  q?: string | null;
  volume_type?: 'claims' | 'patients' | null;
  dateRange?: {
    lte: string;
    gte: string;
  } | null;
  enabledSearchIds?: string[];
}

interface GetTreatingV2Params {
  provider_id: string;
  tableParams: {
    sort?: string;
    sort_id?: string | null;
    sort_field?: string | null;
    order?: string;
    page?: number;
    pageSize?: number;
    q?: string | null;
  };
  filterSets?: number[];
  data?: Record<string, any>;
}

interface GetPacAffiliationsV2Params {
  pacId: string;
  tableParams: {
    sort?: string;
    sort_id?: string | null;
    sort_field?: string | null;
    order?: string;
    page?: number;
    pageSize?: number;
    q?: string | null;
  };
  filterSets?: number[];
  data?: Record<string, any>;
}

// Helper to build query string for referring out
const buildReferringOutQueryString = ({
  page,
  pageSize,
  sort,
  order,
  sort_search,
  sort_value,
  q,
  volume_type,
  dateRange,
  enabledSearchIds = [],
}: Omit<GetReferringOutParams, 'providerId'>): string => {
  const params = new URLSearchParams();

  if (sort) params.append('sort', sort);
  if (order) params.append('order', order);
  if (sort_search) params.append('sort_search', sort_search);
  if (sort_value) params.append('sort_value', sort_value);
  if (page !== undefined) params.append('page', String(page + 1)); // API is 1-indexed
  if (pageSize) params.append('page_size', String(pageSize));
  if (q) params.append('q', q);
  if (volume_type) params.append('volume_type', volume_type);
  if (dateRange) {
    if (dateRange.gte) params.append('gte', dateRange.gte);
    if (dateRange.lte) params.append('lte', dateRange.lte);
  }

  // Add saved search IDs
  enabledSearchIds.forEach((id) => {
    params.append('saved_search', id);
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

// Helper to build query string for V2 endpoints
const buildV2QueryString = (
  tableParams: GetTreatingV2Params['tableParams'],
  filterSets: number[] = []
): string => {
  const params = buildSearchParamsAuto({
    sort: tableParams.sort,
    sort_id: tableParams.sort_id,
    sort_field: tableParams.sort_field,
    order: tableParams.order,
    page: tableParams.page,
    page_size: tableParams.pageSize,
    q: tableParams.q,
  });

  // Add filter set IDs (convert numbers to strings)
  filterSets.forEach((id) => {
    params.append('filter_set', String(id));
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

export const affiliationsApi = createApi({
  reducerPath: 'affiliationsApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [AFFILIATIONS_TAG_TYPE],
  endpoints: (builder) => ({
    // GET referring out affiliations
    getReferringOut: builder.query<
      PaginatedResponse<AffiliationProvider>,
      GetReferringOutParams
    >({
      query: ({ providerId, ...params }) => ({
        url: `v1/entities/entity/${providerId}/affiliations/referring/${buildReferringOutQueryString(params)}`,
        method: 'GET',
      }),
      providesTags: (result, error, { providerId }) => [
        { type: AFFILIATIONS_TAG_TYPE, id: `REFERRING_${providerId}` },
      ],
    }),

    // POST treating V2 affiliations with body
    getTreatingV2: builder.query<
      PaginatedResponse<AffiliationProvider>,
      GetTreatingV2Params
    >({
      query: ({ provider_id, tableParams, filterSets = [], data = {} }) => ({
        url: `v1/entities/v2/entity/${provider_id}/affiliations/treating/${buildV2QueryString(tableParams, filterSets)}`,
        method: 'POST',
        body: data,
      }),
      providesTags: (result, error, { provider_id }) => [
        { type: AFFILIATIONS_TAG_TYPE, id: `TREATING_${provider_id}` },
      ],
    }),

    // POST PAC affiliations V2 with body
    getPacAffiliationsV2: builder.query<
      PaginatedResponse<AffiliationProvider>,
      GetPacAffiliationsV2Params
    >({
      query: ({ pacId, tableParams, filterSets = [], data = {} }) => ({
        url: `v1/entities/v2/hcp-group/${pacId}/affiliations/${buildV2QueryString(tableParams, filterSets)}`,
        method: 'POST',
        body: data,
      }),
      providesTags: (result, error, { pacId }) => [
        { type: AFFILIATIONS_TAG_TYPE, id: `PAC_${pacId}` },
      ],
    }),
  }),
});

export const {
  useGetReferringOutQuery,
  useGetTreatingV2Query,
  useGetPacAffiliationsV2Query,
} = affiliationsApi;
